import { lazy, Suspense, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Palmtree, UtensilsCrossed } from "lucide-react";
import { useSiteSettings, type ContactInfo, type HoursEntry } from "@/hooks/useSiteSettings";
import { SeoOverride } from "@/hooks/useSeoOverride";
import { getCurrentLang } from "@/lib/lang";
import { trackEvent } from "@/lib/tracking";

// Lazy-load below-the-fold sections for mobile performance
const BeachSection = lazy(() => import("@/components/BeachSection"));
const RestaurantSection = lazy(() => import("@/components/RestaurantSection"));
const MenuSection = lazy(() => import("@/components/MenuSection"));
const EventsSection = lazy(() => import("@/components/EventsSection"));
const NewsSection = lazy(() => import("@/components/NewsSection"));
const ContactSection = lazy(() => import("@/components/ContactSection"));
const Footer = lazy(() => import("@/components/Footer"));

const SectionFallback = () => <div className="min-h-[200px]" />;

const Index = () => {
  const { t } = useTranslation();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { data: contact } = useSiteSettings<ContactInfo>("contact");
  const { data: hours } = useSiteSettings<HoursEntry[]>("hours");

  const phone = contact?.phone || "+33 4 93 00 00 00";
  const email = contact?.email || "contact@plagelpc.com";
  const address = contact?.address || "Boulevard de la Croisette, 06400 Cannes";

  const openingHours = (hours || []).map((h) => `${h.label} ${h.hours}`);

  const lang = getCurrentLang();
  const siteDescription = t(
    "hero.defaultDescription",
    "Beach Club & Restaurant sur la Croisette à Cannes. Cuisine méditerranéenne, plage privée et événements."
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": "https://www.plagelpc.com/#website",
        "url": "https://www.plagelpc.com",
        "name": "La Petite Cannoise",
        "description": siteDescription,
        "inLanguage": lang,
        "publisher": {
          "@id": "https://www.plagelpc.com/#organization",
        },
      },
      {
        "@type": "Restaurant",
        "@id": "https://www.plagelpc.com/#organization",
        name: "La Petite Cannoise",
        description: siteDescription,
        url: "https://www.plagelpc.com",
        telephone: phone,
        email: email,
        address: {
          "@type": "PostalAddress",
          streetAddress: address,
          addressLocality: "Cannes",
          postalCode: "06400",
          addressCountry: "FR",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: 43.5513,
          longitude: 7.0128,
        },
        servesCuisine: "Méditerranéenne",
        priceRange: "€€€",
        ...(openingHours.length > 0 && { openingHours }),
      },
    ],
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <SeoOverride path="/" />
      <Header />
      <main>
        <HeroSection />
        <Suspense fallback={<SectionFallback />}>
          <BeachSection />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <RestaurantSection />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <MenuSection />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <EventsSection />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <NewsSection />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <ContactSection />
        </Suspense>
      </main>
      <div className="fixed bottom-5 right-5 z-50">
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="terracotta" size="lg" aria-label={t("contact.stickyReserve")}>
              {t("contact.stickyReserve")}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            side="top"
            sideOffset={12}
            className="w-72 rounded-3xl border border-sage/20 bg-cream p-4 shadow-elegant flex flex-col gap-3"
            onEscapeKeyDown={() => setPopoverOpen(false)}
            onPointerDownOutside={() => setPopoverOpen(false)}
            role="dialog"
            aria-modal="true"
          >
            <p className="font-script text-lg text-terracotta text-center -mb-1">
              {t("contact.stickyReserve")}
            </p>
            <Button
              variant="outline"
              size="lg"
              asChild
              className="w-full text-sm h-auto py-2 px-3 border-sage text-sage hover:bg-sage hover:text-primary-foreground leading-tight"
            >
              <a
                href="https://ext-booking.mysunbed.com/fr/?beachId=67ffc855f779eb743bce6303"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("reservation_cta_click", { section: "sticky_popover", metadata: { target: "beach" } })}
              >
                <Palmtree className="w-4 h-4 mr-1" />
                {t("contact.reserveBeach")}
              </a>
            </Button>
            <Button variant="terracotta" size="lg" asChild className="w-full text-sm h-auto py-2 px-3 leading-tight">
              <a
                href="https://bookings.zenchef.com/results?rid=377206"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("reservation_cta_click", { section: "sticky_popover", metadata: { target: "restaurant" } })}
              >
                <UtensilsCrossed className="w-4 h-4 mr-1" />
                {t("contact.reserveRestaurant")}
              </a>
            </Button>
          </PopoverContent>
        </Popover>
      </div>
      <Suspense fallback={<SectionFallback />}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
